import { useAui, useAuiState } from '@assistant-ui/react';
import { usePrevious, useSessionStorage } from '@mantine/hooks';
import { useEffect } from 'react';

const STORAGE_KEY = 'atlas-last-thread';

/**
 * Persists the active thread ID to sessionStorage and restores it on mount.
 * Must be called from within AssistantRuntimeProvider context.
 */
export function useLastActiveThread() {
  const aui = useAui();
  const isLoading = useAuiState(({ threads }) => threads.isLoading);
  const mainThreadId = useAuiState(({ threads }) => threads.mainThreadId);
  const newThreadId = useAuiState(({ threads }) => threads.newThreadId);
  const [lastThreadId, setLastThreadId] = useSessionStorage<string | null>({
    key: STORAGE_KEY,
    defaultValue: null,
  });
  const previousThreadId = usePrevious(lastThreadId);

  useEffect(() => {
    if (lastThreadId !== mainThreadId && mainThreadId !== newThreadId) {
      // console.debug(`Updating lastThreadId to mainThreadId: ${mainThreadId}`, { threads: { mainThreadId, lastThreadId } });
      setLastThreadId(mainThreadId);
      return;
    }

    if (previousThreadId === null && lastThreadId) {
      // Restore last active thread on initial load
      aui.threads().switchToThread(lastThreadId);
      return;
    }

    if (mainThreadId === newThreadId && lastThreadId && lastThreadId !== mainThreadId) {
      // New chat session started - clear lastThreadId to avoid restoring old thread in new session
      setLastThreadId(null);
      return;
    }

    if (isLoading || !lastThreadId || lastThreadId === mainThreadId) {
      // console.debug('No thread switch needed', { threads: { mainThreadId, lastThreadId } });
      return;
    }

    console.debug(`Restoring last active thread: ${lastThreadId}`, { threads: { mainThreadId, lastThreadId } });

    aui.threads().switchToThread(lastThreadId);
  }, [isLoading, lastThreadId, mainThreadId, newThreadId, aui, setLastThreadId, previousThreadId]);
}
